/**
 * UserSearchModal Component
 *
 * Modal dialog for searching and connecting with other users.
 * Features debounced search, user cards with mutual connections,
 * and optional message when sending connection requests.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  UserPlus,
  Users,
  Loader2,
  Check,
  Send,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  useSearchResults,
  useConnectionLoading,
  useConnectionActions,
  useConnections,
  usePendingSent,
} from '@/stores/connection';
import type { UserSearchResult } from '@/types/connections';

interface UserSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Get display name for a user
 */
const getDisplayName = (user: UserSearchResult): string => {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) {
    return user.firstName;
  }
  return user.username;
};

/**
 * UserSearchResultCard - Individual search result card
 */
interface UserSearchResultCardProps {
  user: UserSearchResult;
  isConnected: boolean;
  isPending: boolean;
  onConnect: (userId: string, message?: string) => void;
  isConnecting: boolean;
}

const UserSearchResultCard: React.FC<UserSearchResultCardProps> = ({
  user,
  isConnected,
  isPending,
  onConnect,
  isConnecting,
}) => {
  const [showMessageInput, setShowMessageInput] = useState(false);
  const [message, setMessage] = useState('');

  const displayName = getDisplayName(user);
  const initials = getInitials(user.firstName, user.lastName, user.username);

  const handleConnect = () => {
    console.log('[UserSearchModal] Connecting to user:', user.id);
    onConnect(user.id, message.trim() || undefined);
    setShowMessageInput(false);
    setMessage('');
  };

  const handleToggleMessage = () => {
    setShowMessageInput(!showMessageInput);
    if (!showMessageInput) {
      setMessage('');
    }
  };

  return (
    <div className="p-4 rounded-lg border border-border bg-card hover:border-border hover:shadow-sm transition-all duration-200">
      <div className="flex items-center gap-4">
        <Avatar className="h-11 w-11">
          <AvatarImage src={user.avatar} alt={displayName} />
          <AvatarFallback className="text-sm font-semibold bg-primary/10 text-primary">
            {initials}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
          <p className="text-xs text-muted-foreground">@{user.username}</p>

          {/* Mutual connections count */}
          {user.mutualConnectionsCount > 0 && (
            <p className="text-xs text-muted-foreground/70 mt-1">
              {user.mutualConnectionsCount} mutual connection{user.mutualConnectionsCount !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isConnected ? (
            <Badge className="text-xs px-3 py-1 bg-primary/10 text-primary">
              <Check className="h-3 w-3 mr-1" />
              Connected
            </Badge>
          ) : isPending ? (
            <Badge
              variant="outline"
              className="text-xs px-3 py-1 border-border"
            >
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              Pending
            </Badge>
          ) : (
            <>
              {!showMessageInput && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-muted-foreground hover:text-foreground"
                  onClick={handleToggleMessage}
                  title="Add a message"
                >
                  <Send className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                size="sm"
                className="h-8 px-4 bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={handleConnect}
                disabled={isConnecting}
              >
                {isConnecting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-1.5" />
                    Connect
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Optional message input */}
      {showMessageInput && !isConnected && !isPending && (
        <div className="mt-4 space-y-3 pt-4 border-t border-border">
          <Textarea
            placeholder="Add a personal message (optional)..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="min-h-[80px] resize-none text-sm bg-background border-border"
            maxLength={500}
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground/70">
              {message.length}/500
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 text-xs text-muted-foreground"
                onClick={handleToggleMessage}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-8 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={handleConnect}
                disabled={isConnecting}
              >
                {isConnecting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <>
                    <Send className="h-3 w-3 mr-1.5" />
                    Send
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * UserSearchModal - Main search modal component
 */
export const UserSearchModal: React.FC<UserSearchModalProps> = ({
  open,
  onOpenChange,
}) => {
  const searchResults = useSearchResults();
  const connections = useConnections();
  const pendingSent = usePendingSent();
  const { isSearching } = useConnectionLoading();
  const { searchUsers, clearSearchResults, sendRequest } = useConnectionActions();

  const [searchQuery, setSearchQuery] = useState('');
  const [connectingUserId, setConnectingUserId] = useState<string | null>(null);

  // Create sets for quick lookup
  const connectedUserIds = useMemo(() => {
    return new Set(connections.map((c) => c.user.id));
  }, [connections]);

  const pendingUserIds = useMemo(() => {
    return new Set(pendingSent.map((p) => p.recipient.id));
  }, [pendingSent]);

  // Debounced search
  useEffect(() => {
    if (!open) {
      return;
    }

    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) {
      clearSearchResults();
      return;
    }

    console.log('[UserSearchModal] Search query:', trimmedQuery);

    const timeoutId = setTimeout(() => {
      console.log('[UserSearchModal] Executing search for:', trimmedQuery);
      searchUsers(trimmedQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, open, searchUsers, clearSearchResults]);

  // Clear search when modal closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      clearSearchResults();
    }
  }, [open, clearSearchResults]);

  // Handle connect
  const handleConnect = useCallback(async (userId: string, message?: string) => {
    console.log('[UserSearchModal] Sending connection request to:', userId);
    setConnectingUserId(userId);
    try {
      await sendRequest(userId, message);
      console.log('[UserSearchModal] Connection request sent successfully');
    } catch (error) {
      console.error('[UserSearchModal] Failed to send connection request:', error);
    } finally {
      setConnectingUserId(null);
    }
  }, [sendRequest]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="light" className="max-w-lg max-h-[80vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-foreground">
            <div className="h-9 w-9 rounded-full flex items-center justify-center bg-primary/10">
              <UserPlus className="h-4 w-4 text-primary" />
            </div>
            Find Connections
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground mt-1 ml-12">
            Search for users by name or username to connect with them
          </DialogDescription>
        </DialogHeader>

        {/* Search input */}
        <div className="px-6 pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or username..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-background border-border focus:border-primary focus:ring-primary/20"
              autoFocus
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-primary" />
            )}
          </div>
        </div>

        {/* Search results */}
        <div className="flex-1 min-h-[200px] max-h-[400px] overflow-y-auto bg-background">
          <div className="p-6 space-y-3">
            {isSearching && searchResults.length === 0 ? (
              /* Loading state */
              <div className="text-center py-12">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary" />
                <p className="text-sm text-muted-foreground">Searching...</p>
              </div>
            ) : searchQuery.trim() && searchResults.length === 0 && !isSearching ? (
              /* Empty state - no results */
              <div className="text-center py-12">
                <div className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-primary/10">
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-base font-medium mb-1 text-foreground">No users found</h3>
                <p className="text-sm text-muted-foreground">
                  Try a different search term
                </p>
              </div>
            ) : !searchQuery.trim() ? (
              /* Empty state - no query */
              <div className="text-center py-12">
                <div className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-primary/10">
                  <Search className="h-8 w-8 text-primary" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Start typing to search for users
                </p>
              </div>
            ) : (
              /* Results list */
              searchResults.map((user) => (
                <UserSearchResultCard
                  key={user.id}
                  user={user}
                  isConnected={connectedUserIds.has(user.id)}
                  isPending={pendingUserIds.has(user.id)}
                  onConnect={handleConnect}
                  isConnecting={connectingUserId === user.id}
                />
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserSearchModal;
